import { buttonBaseClasses, chipClasses, outlinedInputClasses, autocompleteClasses, formLabelClasses, formHelperTextClasses, } from '@mui/material';
import { getDynamicThemeToken } from './theme';
import { generateDynamicThemeToken } from '../../ThemeProvider';
const componentName = 'Autocomplete';
const classPrefix = `ui-Autocomplete`;
export const autocompleteClassesEnum = {
    root: `${classPrefix}-root`,
    addOption: `${classPrefix}-addOption`,
    checkedIcon: `${classPrefix}-checkedIcon`,
    suffixIcon: `${classPrefix}-suffixIcon`,
    chipCloseIcon: `${classPrefix}-ChipCloseIcon`,
    readOnly: `${classPrefix}-readOnly`,
    canCreate: `${classPrefix}-canCreate`,
};
export const optionClasses = {
    paper: `${classPrefix}-paper`,
    small: `${classPrefix}-option-small`,
    medium: `${classPrefix}-option-medium`,
    large: `${classPrefix}-option-large`,
    extraLarge: `${classPrefix}-option-extraLarge`,
    withCheckbox: `${classPrefix}-option-with-checkbox`,
};
export default function getStyle(theme, opts) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const { size = 'medium' } = opts ?? {};
    const textFieldStyle = {
        color: tk(token.textField.color),
        [`& .${outlinedInputClasses.root}`]: {
            borderRadius: tk(token.textField.borderRadius[size]),
            [`&.${autocompleteClasses.inputRoot} .${autocompleteClasses.input}`]: {
                padding: '0 2px',
            },
            [`&.${outlinedInputClasses.disabled}`]: {
                color: tk(token.textField.disabledColor),
            },
        },
        [`& .${outlinedInputClasses.input}`]: {
            fontSize: tk(token.textField.fontSize[size]),
            '&:disabled': {
                color: tk(token.textField.disabledColor),
                textFillColor: tk(token.textField.disabledColor),
            },
            '&::placeholder': {
                color: tk(token.textField.placeholderColor),
                opacity: 1,
            },
            '&:disabled::placeholder': {
                color: tk(token.textField.disabledColor),
                textFillColor: tk(token.textField.disabledColor),
            },
        },
        [`& .${outlinedInputClasses.root}:not(.${outlinedInputClasses.error}) .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: `${tk(token.textField.borderColor)}`,
        },
        [`& .${outlinedInputClasses.root}.${outlinedInputClasses.disabled} .${outlinedInputClasses.notchedOutline} `]: {
            borderColor: `${tk(token.border.disabled)}`,
        },
        [`& .${outlinedInputClasses.root}.${outlinedInputClasses.error} .${outlinedInputClasses.notchedOutline} `]: {
            borderColor: `${tk(token.border.error.default)}`,
        },
        [`& .${outlinedInputClasses.root}.${outlinedInputClasses.error}.${outlinedInputClasses.disabled} .${outlinedInputClasses.notchedOutline} `]: {
            borderColor: `${tk(token.border.error.disabled)}`,
        },
        [`& .${outlinedInputClasses.notchedOutline}`]: {
            top: 0,
            borderColor: `${tk(token.textField.borderColor)}`,
            borderWidth: '1px !important',
            legend: {
                display: 'none !important',
            },
        },
    };
    const formHelperTextStyle = {
        [`&.${formHelperTextClasses.root}`]: {
            fontFamily: tk(token.fontFamily),
            margin: '2px 0 0',
            color: tk(token.helpText.default),
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            lineHeight: 'unset',
            [`&.${formHelperTextClasses.error}`]: {
                display: 'flex',
                alignItems: 'center',
                color: tk(token.helpText.error),
                lineHeight: '20px',
            },
        },
    };
    const paperStyle = {
        marginTop: '4px',
        color: tk(token.color.neutral.stronger),
        fontSize: 14,
        borderRadius: '8px',
        background: tk(token.paper.background),
        boxShadow: tk(token.boxShadow.pager),
        fontFamily: tk(token.fontFamily),
        padding: '8px',
        border: `0.5px solid ${tk(token.border.secondary.paper)} !important`,
        paddingBottom: '8px',
        '&:empty': {
            // https://github.com/mui/material-ui/issues/40843
            display: 'none',
        },
        // The following are dealing with situations where there's no matched items and creatable is true.
        // The `:has()` is not well-supported on Firefox currently but unluckily we do not have any better ways using MUI.
        // This will cause a slight spacing above the adding label on Firefox in that situation.
        [`&:has(.${autocompleteClasses.listbox} > li:only-child)`]: {
            paddingBottom: '8px',
        },
        [`&.${autocompleteClassesEnum.canCreate}`]: {
            paddingBottom: '52px',
            // // The following are dealing with situations where there's no matched items and creatable is true.
            // // The `:has()` is not well-supported on Firefox currently but unluckily we do not have any better ways using MUI.
            // // This will cause a slight spacing above the adding label on Firefox in that situation.
            [`&:has(.${autocompleteClasses.listbox} > li:only-child)`]: {
                paddingBottom: '44px',
            },
        },
        [`& .${autocompleteClasses.listbox}`]: {
            position: 'static',
            padding: 0,
            '>li': {
                '&:first-of-type': {
                    [`.${autocompleteClasses.groupLabel}`]: {
                        marginTop: 0,
                        paddingTop: '8px',
                        borderTop: 'none',
                    },
                },
            },
            [`& .${autocompleteClasses.option}`]: {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                lineHeight: '20px',
                overflowWrap: 'break-word',
                minHeight: 0,
                padding: '7px 8px',
                borderRadius: '4px',
                [`.${autocompleteClassesEnum.checkedIcon}`]: {
                    color: tk(token.color.primary.text),
                    fontSize: 16,
                    marginInlineStart: '8px',
                },
            },
            [`& .${autocompleteClasses.option}[aria-selected="true"]`]: {
                backgroundColor: 'transparent',
            },
            [`& .${autocompleteClasses.option}.${autocompleteClasses.focused}`]: {
                backgroundColor: tk(token.background.secondary.hover),
            },
            [`& .${autocompleteClasses.option}:active`]: {
                backgroundColor: tk(token.background.secondary.active),
            },
            [`& .${autocompleteClasses.option}[aria-selected="true"].${autocompleteClasses.focused}`]: {
                backgroundColor: tk(token.background.secondary.hover),
                '&:active': {
                    backgroundColor: tk(token.background.secondary.active),
                },
            },
            [`& .${autocompleteClasses.option}[aria-disabled="true"]`]: {
                color: tk(token.textField.disabledColor),
                opacity: 1,
            },
            [`.${autocompleteClassesEnum.addOption}`]: {
                position: 'absolute',
                left: '8px',
                right: '8px',
                bottom: '8px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                borderTop: `1px solid ${tk(token.paper.borderColor)}`,
                color: tk(token.option.color),
                justifyContent: 'space-between',
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                [`&:hover, &.${autocompleteClasses.focused}`]: {
                    borderTopColor: 'transparent',
                    borderRadius: '4px',
                },
            },
            [`& .${autocompleteClasses.groupLabel}`]: {
                background: tk(token.paper.background),
                marginTop: '8px',
                padding: '8px',
                paddingTop: '16px',
                fontSize: tk(token.group.title.fontSize),
                fontWeight: tk(token.group.title.fontWeight),
                lineHeight: tk(token.group.title.lineHeight),
                borderTop: `1px solid ${tk(token.group.borderColor)}`,
                color: tk(token.group.title.color),
                '&:empty': {
                    display: 'none',
                },
            },
        },
        [`&.${optionClasses.small}`]: {
            [`.${autocompleteClasses.listbox}`]: {
                [`.${autocompleteClasses.option}`]: {
                    lineHeight: '16px',
                    [`&.${optionClasses.withCheckbox}`]: {
                        lineHeight: '20px',
                    },
                    [`.${autocompleteClassesEnum.checkedIcon}`]: {
                        fontSize: '14px',
                    },
                },
            },
        },
        [`.${autocompleteClasses.noOptions}`]: {
            fontSize: 14,
            textAlign: 'center',
            padding: '8px',
            lineHeight: '20px',
            color: tk(token.color.neutral.default),
        },
        [`&.${optionClasses.paper} .${autocompleteClasses.noOptions}`]: {
            lineHeight: '16px',
        },
        [`&.${optionClasses.small} `]: {
            [`.${autocompleteClasses.noOptions}`]: {
                fontSize: 12,
            },
            [`.${autocompleteClasses.option}`]: {
                padding: '6px 8px',
                fontSize: 12,
            },
        },
        [`&.${optionClasses.medium} `]: {
            [`.${autocompleteClasses.noOptions}`]: {
                fontSize: 14,
            },
            [`.${autocompleteClasses.option}`]: {
                fontSize: 14,
                lineHeight: '18px',
            },
            [`.${optionClasses.withCheckbox}`]: {
                lineHeight: '20px',
                padding: '6px 8px',
            },
        },
        [`&.${optionClasses.large} `]: {
            [`.${autocompleteClasses.noOptions}`]: {
                fontSize: 14,
            },
            [`.${autocompleteClasses.option}`]: {
                fontSize: 16,
                [`&.${optionClasses.withCheckbox}`]: {
                    lineHeight: '20px',
                },
            },
        },
        [`&.${optionClasses.extraLarge} `]: {
            [`.${autocompleteClasses.noOptions}`]: {
                fontSize: 14,
            },
            [`.${autocompleteClasses.option}`]: {
                fontSize: 16,
                lineHeight: '18px',
                padding: '7px 8px',
                [`&.${optionClasses.withCheckbox}`]: {
                    lineHeight: '20px',
                    padding: '6px 8px',
                },
            },
        },
        [`&.${optionClasses.large} .${autocompleteClasses.noOptions}, &.${optionClasses.extraLarge} .${autocompleteClasses.noOptions}`]: {
            fontSize: 16,
        },
    };
    const style = {
        [`.${autocompleteClasses.inputRoot}`]: {
            background: tk(token.background.secondary.default),
            [`.${autocompleteClasses.input}`]: {
                height: `${tk(token.textField.height[size])}`,
                lineHeight: `${tk(token.textField.height[size])}`,
            },
        },
        [`&:hover:not(.${outlinedInputClasses.focused})`]: {
            [`.${autocompleteClasses.inputRoot}:not(.${outlinedInputClasses.disabled}, .${autocompleteClassesEnum.readOnly} *)`]: {
                background: tk(token.background.secondary.hover),
            },
        },
        [`.${autocompleteClasses.tag}`]: {
            marginTop: 0,
            marginBottom: 0,
            lineHeight: '20px',
            fontSize: 14,
        },
        [`& .${autocompleteClasses.inputRoot}`]: {
            maxHeight: '124px',
            overflow: 'auto',
            position: 'static',
            [`& .${autocompleteClasses.input}`]: {
                minWidth: 50,
            },
        },
        [`&.${outlinedInputClasses.focused} .${autocompleteClasses.inputRoot}`]: {
            outline: tk(token.outline.focus),
            outlineOffset: tk(token.outlineOffset),
            borderRadius: tk(token.textField.borderRadius[size]),
            borderColor: tk(token.textField.borderColor),
        },
        [`&.${autocompleteClasses.hasClearIcon} .${outlinedInputClasses.sizeSmall}`]: {
            paddingRight: '39px',
        },
        [`&.${autocompleteClasses.hasPopupIcon} .${outlinedInputClasses.sizeSmall}`]: {
            paddingRight: '39px',
        },
        [`& .${formLabelClasses.root} + div`]: {
            position: 'relative',
        },
        [`& .${outlinedInputClasses.root}.${autocompleteClasses.inputRoot}`]: {
            py: '5px',
            pl: size === 'large' || size === 'extraLarge' ? '12px' : '8px',
            borderRadius: tk(token.textField.borderRadius[size]),
            minHeight: tk(token.textField.height[size]),
            gap: '6px',
            boxSizing: 'border-box',
        },
        [`& .${outlinedInputClasses.root}.${autocompleteClasses.inputRoot} input.${autocompleteClasses.input}`]: {
            padding: '0 0 0 6px',
            '&:first-of-type': {
                padding: 0,
            },
        },
        [`& .${autocompleteClasses.endAdornment}`]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            top: 0,
            transform: 'translate(0)',
            '& > button': {
                verticalAlign: 'unset',
                padding: 0,
                borderRadius: '4px',
                background: 'transparent',
            },
            [`& .${autocompleteClassesEnum.suffixIcon}`]: {
                color: tk(token.color.neutral.stronger),
                transform: 'rotate(90deg)',
                fontSize: tk(token.icon.size[size]),
            },
            [`.${buttonBaseClasses.disabled} .${autocompleteClassesEnum.suffixIcon}`]: {
                color: tk(token.textField.disabledColor),
            },
        },
        [`& .${autocompleteClasses.clearIndicator}`]: {
            visibility: 'visible',
            marginInlineEnd: '4px',
            '&:hover': {
                backgroundColor: 'unset',
            },
        },
        [`&:focus-within .${autocompleteClasses.clearIndicator}`]: {
            visibility: 'visible',
        },
        [`.${chipClasses.root}`]: {
            margin: '0',
        },
        [`.${outlinedInputClasses.root} .${autocompleteClasses.endAdornment}`]: {
            right: size === 'large' || size === 'extraLarge' ? '12px' : '8px',
        },
        [`.${autocompleteClasses.popupIndicator}`]: {
            margin: 0,
        },
        [`&.${autocompleteClassesEnum.readOnly}`]: {
            [`.${outlinedInputClasses.root}:not(.${outlinedInputClasses.disabled})`]: {
                color: tk(token.textField.readOnlyColor),
                background: tk(token.background.secondary.readOnly),
                [`.${autocompleteClasses.popupIndicator}`]: {
                    display: 'none',
                },
                [`.${chipClasses.label}`]: {
                    color: tk(token.chip.readOnlyColor),
                },
            },
        },
    };
    return { formHelperTextStyle, paperStyle, textFieldStyle, style };
}
